import type { TelErrorEvent, TelStorageEventHandlers, TraceData } from '@w/tel'
import type { SerializableErrorData } from '@w/utils'
import { toastManager } from '@w/ui/components/toast'

/** Span name → what the user was doing (or what ran for them in the background). */
const operationLabels: Record<string, string> = {
  'embeddings.server.index': 'Search indexing failed',
  'embeddings.index': 'Search indexing failed',
}

/**
 * Every error that reaches the client root tel becomes one visible notification: what failed (the operation of the
 * span it was recorded in), why (the error and its causes), and a Details disclosure with the full error.
 * Tel storage dedupes before `errors.onCreate` (runtime-storage.md › Error and warning notifications): the same
 * error, or a structurally equal one within five minutes, notifies once, so a retry storm shows once.
 */
export function errorNotifications(manager: Pick<typeof toastManager, 'add'> = toastManager): TelStorageEventHandlers {
  return { errors: { onCreate(event) { manager.add(errorToast(event)) } } }
}

export function errorToast(event: TelErrorEvent) {
  const span = event.data.spanId ? findSpan(event.traces, event.data.spanId) : undefined
  const chain = causeChain(event.data.error)
  return {
    type: 'error' as const,
    title: (span && operationLabels[span.name]) ?? 'Something failed',
    description: <span data-testid="error-notification">
      <span data-testid="error-notification-reason">{chain.map(error => error.message).join(': ')}</span>
      {/* Swipe-ignore: the toast root captures the pointer for swipe-to-dismiss, which swallows a summary click. */}
      <details data-base-ui-swipe-ignore="">
        <summary>Details</summary>
        <pre className="whitespace-pre-wrap">{chain.map(error => `${error.type}: ${error.message}${error.details === undefined ? '' : `\n${JSON.stringify(error.details)}`}`).join('\ncaused by ')}</pre>
      </details>
    </span>,
  }
}

function causeChain(error: SerializableErrorData): SerializableErrorData[] {
  const chain: SerializableErrorData[] = []
  for (let current: SerializableErrorData | undefined = error; current && chain.length < 5; current = current.cause) chain.push(current)
  return chain
}

function findSpan(traces: TraceData[], spanId: string): TraceData | undefined {
  for (const trace of traces) {
    if (trace.id === spanId) return trace
    const found = findSpan(trace.children, spanId)
    if (found) return found
  }
  return undefined
}
